/**
 * Text Column Pattern
 * 
 * Creates text columns with smart defaults for truncation, formatting, and display.
 */

import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { TABLE_SETTINGS } from '../../constants/tableSettings';
import type { TextColumnOptions } from './types';

/**
 * Creates a text column with smart defaults and optional customization
 * 
 * @param accessor - The data accessor key
 * @param options - Optional customization options
 * @returns ColumnDef for TanStack Table
 * 
 * @example
 * // Basic usage - just the accessor
 * textColumn('name')
 * 
 * // With customization
 * textColumn('description', {
 *   maxLength: 100,
 *   showTooltip: true,
 *   transform: (value) => value.toUpperCase()
 * })
 */
export function textColumn<T>(
  accessor: keyof T,
  options: TextColumnOptions = {}
): ColumnDef<T> {
  const {
    header = String(accessor),
    sortable = true,
    resizable = true,
    maxLength = TABLE_SETTINGS.text.maxLength,
    transform,
    emptyDisplay = TABLE_SETTINGS.text.emptyDisplay,
    className = TABLE_SETTINGS.text.className,
    width,
    maxWidth,
    minWidth,
  } = options;

  return {
    id: String(accessor),
    header,
    accessorKey: accessor,
    enableSorting: sortable,
    enableResizing: resizable,
    size: width,
    maxSize: maxWidth,
    minSize: minWidth,
    cell: ({ row }) => {
      // Use custom getValue function if provided, otherwise use accessor
      let value = options.getValue 
        ? options.getValue(row.original)
        : row.original[accessor] as string;
      
      // Handle empty/null values
      if (!value || value === '') {
        return emptyDisplay;
      }
      
      // Apply transformation if provided
      if (transform) {
        value = transform(value);
      }
      
      // Handle truncation
      const shouldTruncate = maxLength && value.length > maxLength;
      const displayValue = shouldTruncate 
        ? `${value.substring(0, maxLength)}...`
        : value;
      
      // Return styled element with good default styling
      return React.createElement('div', {
        className: className
      }, displayValue);
    },
  };
}

/**
 * Creates a basic text column configuration (simplified)
 * This is a helper for quick text column setup
 */
export function simpleTextColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return {
    id: String(accessor),
    header: header || String(accessor),
    accessorKey: accessor,
    enableSorting: true,
    cell: ({ row }) => {
      const value = row.original[accessor];
      return String(value || TABLE_SETTINGS.text.emptyDisplay);
    },
  };
} 